/**
 * Le référentiel des offres (ADR 0005) — tenu par la Direction et la
 * chargée de clientèle, lu par la fiche de souscription et par l'accueil.
 *
 * Une offre porte les treize prestations des fiches papier d'AFS
 * (`AFS Data/offre/`) : dix sont couvertes partout, trois font l'offre
 * (hospitalisation, lunetterie, accouchement) avec leur plafond en note.
 */
import { z } from 'zod';
export const PRESTATIONS_OFFRE = [
    'CONSULTATION_GENERALE',
    'CONSULTATION_SPECIALISTE',
    'SOINS_DENTAIRES',
    'VISITE_EMBAUCHE_SYSTEMATIQUE',
    'ANALYSES_COMPLEMENTAIRES',
    'ECHOGRAPHIE',
    'VACCINATION_PEV',
    'ACCOMPAGNEMENT_MATERNITE',
    'CONSEILS_HYGIENE',
    'OUVERTURE_ANTENNE',
    'HOSPITALISATION',
    'LUNETTERIE',
    'ACCOUCHEMENT_NORMAL',
];
export const LIBELLE_PRESTATION_OFFRE = {
    CONSULTATION_GENERALE: 'Consultation générale',
    CONSULTATION_SPECIALISTE: 'Consultation générale chez un médecin spécialiste',
    SOINS_DENTAIRES: 'Soins dentaires (soins et extraction)',
    VISITE_EMBAUCHE_SYSTEMATIQUE: 'Visite médicale d’embauche / visite systématique',
    ANALYSES_COMPLEMENTAIRES: 'Analyses complémentaires : EEG, ECG, radiographie',
    ECHOGRAPHIE: 'Échographie',
    VACCINATION_PEV: 'Vaccination selon le PEV',
    ACCOMPAGNEMENT_MATERNITE: 'Accompagnement à la maternité',
    CONSEILS_HYGIENE: 'Conseils en matière d’hygiène',
    OUVERTURE_ANTENNE: 'Ouverture d’une antenne (à partir de 500 employés)',
    HOSPITALISATION: 'Hospitalisation',
    LUNETTERIE: 'Lunetterie',
    ACCOUCHEMENT_NORMAL: 'Accouchement normal',
};
/** Les trois prestations qui varient d'une offre à l'autre : en tête de l'écran. */
export const PRESTATIONS_VARIABLES = [
    'HOSPITALISATION',
    'LUNETTERIE',
    'ACCOUCHEMENT_NORMAL',
];
export const schemaLigneCouverture = z.object({
    prestation: z.enum(PRESTATIONS_OFFRE),
    couverte: z.boolean(),
    /** Plafond, durée, condition — tel qu'écrit sur la fiche. */
    note: z.string().trim().max(200).nullable(),
});
export const schemaCouverture = z.array(schemaLigneCouverture).max(PRESTATIONS_OFFRE.length);
/** La couverture par défaut d'une offre nouvelle : les dix communes couvertes, les trois autres non. */
export function couvertureParDefaut() {
    return PRESTATIONS_OFFRE.map((prestation) => ({
        prestation,
        couverte: !PRESTATIONS_VARIABLES.includes(prestation),
        note: null,
    }));
}
/** Une couverture stockée, complétée des prestations qu'elle ne nomme pas. */
export function couvertureComplete(lignes) {
    const par = new Map((lignes ?? []).map((l) => [l.prestation, l]));
    return couvertureParDefaut().map((defaut) => par.get(defaut.prestation) ?? defaut);
}
/**
 * Décisions 338 et 339 — les prestations qu'une offre ouvre à un patient
 * couvert, et le forfait payé à la caisse pour chacune (`null` : rien à
 * payer). La liste absente (`null`) ouvre tout, sans forfait.
 */
export const schemaPrestationAccessible = z.object({
    prestation: z.string().trim().toUpperCase().min(1).max(60),
    forfait: z.number().int().min(1).max(10_000_000).nullable(),
});
export const schemaPrestationsAccessibles = z
    .array(schemaPrestationAccessible)
    .min(1, 'Au moins une prestation : sinon, retirez la restriction.')
    .max(30)
    .refine((lignes) => new Set(lignes.map((l) => l.prestation)).size === lignes.length, 'Une prestation apparaît deux fois.')
    .nullable();
export const schemaOffreDetaillee = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
    actif: z.boolean(),
    /**
     * Le rang (écart d'offre) : la meilleure de deux offres est celle de rang le
     * plus haut. `null` : offre fermée, ou pas encore placée — la plus basse.
     */
    rang: z.number().int().nullable(),
    couverture: z.array(schemaLigneCouverture),
    /** Décision 338 — prestations ouvertes et forfaits ; `null` : toutes, rien à payer. */
    prestationsAccessibles: schemaPrestationsAccessibles,
    /** Contrats actifs qui la portent : une offre portée ne se ferme pas à la légère. */
    nbContratsActifs: z.number().int(),
});
export const schemaCreationOffre = z
    .object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9][A-Z0-9_+-]{0,29}$/, 'Code : lettres, chiffres, tirets, « + ».'),
    nom: z.string().trim().min(1, 'Nom requis.').max(80),
    couverture: schemaCouverture.optional(),
})
    .strict();
export const schemaModificationOffre = z
    .object({
    nom: z.string().trim().min(1).max(80).optional(),
    actif: z.boolean().optional(),
    couverture: schemaCouverture.optional(),
    prestationsAccessibles: schemaPrestationsAccessibles.optional(),
})
    .strict();
/**
 * Le classement des offres, d'un seul geste : toutes les offres proposées, de
 * la plus basse à la plus haute. `classementConnu` est l'ordre que l'écran a
 * lu : s'il a changé entre-temps (une autre chargée vient d'enregistrer), le
 * serveur refuse plutôt que d'écraser son travail.
 */
export const schemaClassementOffres = z
    .object({
    offreIds: z
        .array(z.uuid())
        .min(1)
        .max(200)
        .refine((ids) => new Set(ids).size === ids.length, 'Une offre apparaît deux fois.'),
    classementConnu: z.array(z.uuid()).max(200),
})
    .strict();
/**
 * Une offre et son rang, sans sa couverture : ce que l'accueil et la fiche
 * patient lisent pour situer une offre (« 5/7 ») et choisir l'offre lue sur
 * une carte. Les offres proposées seulement, de la plus basse à la plus haute.
 */
export const schemaOffreClassee = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
    rang: z.number().int().nullable(),
});
//# sourceMappingURL=offres.js.map